import { jsxs, jsx } from 'react/jsx-runtime';
import { styled } from '@flex-design-system/stitches-react';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { useRef } from 'react';
import { Direction } from './types.mjs';
import { clamp } from './utils.mjs';

const MIN_WIDTH = 50;
const MIN_HEIGHT = 50;
function ImageResizer({
  imageTarget,
  maxWidth,
  onResizeStart,
  onResizeEnd
}) {
  const [editor] = useLexicalComposerContext();
  const resizingStateRef = useRef(null);
  const handlePointerDown = (event, direction) => {
    if (!imageTarget || !editor.isEditable()) {
      return;
    }
    const { width, height } = imageTarget.getBoundingClientRect();
    resizingStateRef.current = {
      startWidth: width,
      startHeight: height,
      currentWidth: width,
      currentHeight: height,
      ratio: height / width,
      startX: event.clientX,
      startY: event.clientY,
      direction
    };
    imageTarget.style.width = `${width}px`;
    imageTarget.style.height = `${height}px`;
    onResizeStart();
    document.addEventListener("pointermove", handlePointerMove);
    document.addEventListener("pointerup", handlePointerUp);
  };
  const handlePointerMove = (event) => {
    if (!imageTarget || !resizingStateRef.current) {
      return;
    }
    const { direction, startWidth, startHeight, startX, startY, ratio } = resizingStateRef.current;
    const isHorizontal = Boolean(direction & (Direction.east | Direction.west));
    const isVertical = Boolean(direction & (Direction.north | Direction.south));
    if (isHorizontal && isVertical) {
      const diff = startX - event.clientX;
      const dx = direction & Direction.east ? -diff : diff;
      const width = clamp(startWidth + dx, MIN_WIDTH, maxWidth);
      const height = width * ratio;
      imageTarget.style.width = `${width}px`;
      imageTarget.style.height = `${height}px`;
      resizingStateRef.current.currentWidth = width;
      resizingStateRef.current.currentHeight = height;
    } else if (isHorizontal) {
      const diff = startX - event.clientX;
      const dx = direction & Direction.east ? -diff : diff;
      const width = clamp(startWidth + dx, MIN_WIDTH, maxWidth);
      imageTarget.style.width = `${width}px`;
      resizingStateRef.current.currentWidth = width;
    } else if (isVertical) {
      const diff = startY - event.clientY;
      const dy = direction & Direction.south ? -diff : diff;
      const height = Math.max(startHeight + dy, MIN_HEIGHT);
      imageTarget.style.height = `${height}px`;
      resizingStateRef.current.currentHeight = height;
    }
  };
  const handlePointerUp = () => {
    if (!imageTarget || !resizingStateRef.current) {
      return;
    }
    const { currentWidth, currentHeight } = resizingStateRef.current;
    onResizeEnd(currentWidth, currentHeight);
    document.removeEventListener("pointermove", handlePointerMove);
    document.removeEventListener("pointerup", handlePointerUp);
    resizingStateRef.current = null;
  };
  return /* @__PURE__ */ jsxs("div", { children: [
    /* @__PURE__ */ jsx(
      Resizer,
      {
        direction: "n",
        onPointerDown: (e) => handlePointerDown(e, Direction.north)
      }
    ),
    /* @__PURE__ */ jsx(
      Resizer,
      {
        direction: "e",
        onPointerDown: (e) => handlePointerDown(e, Direction.east)
      }
    ),
    /* @__PURE__ */ jsx(
      Resizer,
      {
        direction: "s",
        onPointerDown: (e) => handlePointerDown(e, Direction.south)
      }
    ),
    /* @__PURE__ */ jsx(
      Resizer,
      {
        direction: "w",
        onPointerDown: (e) => handlePointerDown(e, Direction.west)
      }
    ),
    /* @__PURE__ */ jsx(
      Resizer,
      {
        direction: "ne",
        onPointerDown: (e) => handlePointerDown(e, Direction.north | Direction.east)
      }
    ),
    /* @__PURE__ */ jsx(
      Resizer,
      {
        direction: "se",
        onPointerDown: (e) => handlePointerDown(e, Direction.south | Direction.east)
      }
    ),
    /* @__PURE__ */ jsx(
      Resizer,
      {
        direction: "sw",
        onPointerDown: (e) => handlePointerDown(e, Direction.south | Direction.west)
      }
    ),
    /* @__PURE__ */ jsx(
      Resizer,
      {
        direction: "nw",
        onPointerDown: (e) => handlePointerDown(e, Direction.north | Direction.west)
      }
    )
  ] });
}
const Resizer = styled("div", {
  position: "absolute",
  display: "block",
  width: 8,
  height: 8,
  backgroundColor: "$cosmicBlue",
  border: "1px solid $white",
  variants: {
    direction: {
      n: {
        top: -5,
        left: "calc(50% - 4px)",
        cursor: "ns-resize"
      },
      e: {
        top: "calc(50% - 4px)",
        right: -5,
        cursor: "ew-resize"
      },
      s: {
        bottom: -5,
        left: "calc(50% - 4px)",
        cursor: "ns-resize"
      },
      w: {
        top: "calc(50% - 4px)",
        left: -5,
        cursor: "ew-resize"
      },
      ne: {
        top: -5,
        right: -5,
        cursor: "ne-resize"
      },
      se: {
        bottom: -5,
        right: -5,
        cursor: "nwse-resize"
      },
      sw: {
        bottom: -5,
        left: -5,
        cursor: "sw-resize"
      },
      nw: {
        top: -5,
        left: -5,
        cursor: "nw-resize"
      }
    }
  }
});

export { ImageResizer as default };
//# sourceMappingURL=ImageResizer.mjs.map
